import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Calendar as CalendarIcon, Search, List, Clock, CheckCircle2, Wrench, Eye, Edit, Trash2 } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import Select from '../../components/Select';
import Table from '../../components/Table';
import RowActions from '../../components/RowActions';
import Modal from '../../components/Modal';
import Calendar from '../../components/Calendar';
import ConfirmDialog from '../../components/ConfirmDialog';
import { apiService } from '../../services/apiService';
import { formatDate, renderCell, isEmptyCell, renderCellDate } from '../../utils/helpers';
import { useToast } from '../../contexts/ToastContext';

const MaintenanceManagement = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [maintenances, setMaintenances] = useState([]);
  const [filteredMaintenances, setFilteredMaintenances] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [detailsModal, setDetailsModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, maintenanceId: null, maintenanceName: '' });
  const [clients, setClients] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    equipmentId: '',
    type: 'routine',
    scheduledDate: '',
    status: 'pending',
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterMaintenances();
  }, [searchQuery, statusFilter, maintenances]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [maintenanceData, clientData, equipmentData] = await Promise.all([
        apiService.getMaintenances(),
        apiService.getClients(),
        apiService.getEquipment(),
      ]);
      setMaintenances(maintenanceData);
      setClients(clientData);
      setEquipment(equipmentData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toastError('Impossible de charger les maintenances');
    } finally {
      setLoading(false);
    }
  };

  const filterMaintenances = () => {
    let filtered = [...maintenances];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.equipmentName?.toLowerCase().includes(query) ||
          item.clientName?.toLowerCase().includes(query)
      );
    }

    if (statusFilter) {
      filtered = filtered.filter((item) => item.status === statusFilter);
    }

    setFilteredMaintenances(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await apiService.createMaintenance(formData);
      setAddModal(false);
      setFormData({ equipmentId: '', type: 'routine', scheduledDate: '', status: 'pending', notes: '' });
      await loadData();
      toastSuccess('Maintenance planifiée avec succès !');
    } catch (error) {
      console.error('Failed to create maintenance:', error);
      toastError('Erreur lors de la planification de la maintenance.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (maintenance) => {
    setSelectedMaintenance(maintenance);
    setFormData({
      equipmentId: maintenance.equipmentId,
      type: maintenance.type,
      scheduledDate: maintenance.scheduledDate?.split('T')[0] || '',
      status: maintenance.status,
      notes: maintenance.notes || '',
    });
    setEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await apiService.updateMaintenance(selectedMaintenance.id, formData);
      setEditModal(false);
      setSelectedMaintenance(null);
      setFormData({ equipmentId: '', type: 'routine', scheduledDate: '', status: 'pending', notes: '' });
      await loadData();
      toastSuccess('Maintenance mise à jour avec succès !');
    } catch (error) {
      console.error('Failed to update maintenance:', error);
      toastError('Erreur lors de la mise à jour de la maintenance.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (maintenance) => {
    setDeleteConfirm({
      open: true,
      maintenanceId: maintenance.id,
      maintenanceName: `${maintenance.equipmentName} - ${maintenance.type}`,
    });
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteMaintenance(deleteConfirm.maintenanceId);
      setDeleteConfirm({ open: false, maintenanceId: null, maintenanceName: '' });
      await loadData();
      toastSuccess('Maintenance supprimée avec succès !');
    } catch (error) {
      console.error('Failed to delete maintenance:', error);
      toastError('Erreur lors de la suppression de la maintenance.');
      setDeleteConfirm({ open: false, maintenanceId: null, maintenanceName: '' });
    }
  };

  const handleMaintenanceClick = (maintenance) => {
    setSelectedMaintenance(maintenance);
    setDetailsModal(true);
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      scheduled: 'Planifiée',
      in_progress: 'En cours',
      completed: 'Terminée',
      cancelled: 'Annulée',
    };
    return labels[status] || status;
  };

  const columns = [
    {
      header: 'Équipement',
      accessor: 'equipmentName',
      render: (row) => (
        <div>
          <p className="font-medium text-gray-900">{renderCell(row.equipmentName)}</p>
          <p className="text-sm text-gray-500">{renderCell(row.serialNumber)}</p>
        </div>
      ),
    },
    {
      header: 'Client',
      accessor: 'clientName',
    },
    {
      header: 'Type',
      accessor: 'type',
      render: (row) => (
        isEmptyCell(row.type) ? renderCell(row.type) : (
        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
          {row.type}
        </span>
        )
      ),
    },
    {
      header: 'Date prévue',
      accessor: 'scheduledDate',
      render: (row) => renderCellDate(row.scheduledDate),
    },
    {
      header: 'Statut',
      accessor: 'status',
      render: (row) => {
        const colors = {
          pending: 'bg-yellow-100 text-yellow-700',
          scheduled: 'bg-blue-100 text-blue-700',
          in_progress: 'bg-purple-100 text-purple-700',
          completed: 'bg-green-100 text-green-700',
          cancelled: 'bg-red-100 text-red-700',
        };
        const label = getStatusLabel(row.status);
        if (isEmptyCell(row.status)) return renderCell(row.status);
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors[row.status] || 'bg-gray-100 text-gray-700'}`}>
            {label}
          </span>
        );
      },
    },
    {
      header: 'Actions',
      accessor: 'actions',
      render: (row) => (
        <RowActions
          onView={() => handleMaintenanceClick(row)}
          onEdit={() => handleEdit(row)}
          onDelete={() => handleDeleteClick(row)}
        />
      ),
    },
  ];

  const statusOptions = [
    { value: '', label: 'Tous les statuts' },
    { value: 'pending', label: 'En attente' },
    { value: 'scheduled', label: 'Planifiées' },
    { value: 'in_progress', label: 'En cours' },
    { value: 'completed', label: 'Terminées' },
    { value: 'cancelled', label: 'Annulées' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Calculate stats
  const totalMaintenances = maintenances.length;
  const pendingMaintenances = maintenances.filter(m => m.status === 'pending').length;
  const completedMaintenances = maintenances.filter(m => m.status === 'completed').length;
  const upcomingMaintenances = maintenances.filter(m => {
    const scheduledDate = new Date(m.scheduledDate);
    const now = new Date();
    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return m.status === 'pending' && scheduledDate >= now && scheduledDate <= weekFromNow;
  }).length;

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Gestion des Maintenances</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Planifiez et suivez les maintenances</p>
        </div>
        <Button variant="primary" icon={Plus} onClick={() => setAddModal(true)} className="w-full sm:w-auto">
          Planifier une maintenance
        </Button>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-blue-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg sm:rounded-xl shadow-lg shadow-blue-500/30">
                <Wrench className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Total Maintenances</p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{totalMaintenances}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-amber-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg sm:rounded-xl shadow-lg shadow-amber-500/30">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-amber-600 bg-amber-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                {totalMaintenances > 0 ? Math.round((pendingMaintenances / totalMaintenances) * 100) : 0}%
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">En Attente</p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{pendingMaintenances}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-green-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg sm:rounded-xl shadow-lg shadow-green-500/30">
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-green-600 bg-green-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                {totalMaintenances > 0 ? Math.round((completedMaintenances / totalMaintenances) * 100) : 0}%
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Terminées</p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{completedMaintenances}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-purple-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg sm:rounded-xl shadow-lg shadow-purple-500/30">
                <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-purple-600 bg-purple-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                7 jours
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">À Venir</p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{upcomingMaintenances}</p>
          </div>
        </motion.div>
      </div>

      {/* Search and Filters */}
      <Card>
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={Search}
            />
          </div>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={statusOptions}
            className="w-64"
          />
          
          {/* View Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                viewMode === 'list'
                  ? 'bg-white text-red-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="w-4 h-4" />
              <span className="font-medium">Liste</span>
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                viewMode === 'calendar'
                  ? 'bg-white text-red-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <CalendarIcon className="w-4 h-4" />
              <span className="font-medium">Calendrier</span>
            </button>
          </div>
        </div>
      </Card>

      {/* View Content */}
      {viewMode === 'list' ? (
        <Card>
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {filteredMaintenances.length} maintenance(s)
            </h2>
          </div>
          <Table columns={columns} data={filteredMaintenances} />
        </Card>
      ) : (
        <Calendar 
          maintenances={filteredMaintenances} 
          onMaintenanceClick={handleMaintenanceClick}
        />
      )}

      {/* Add Maintenance Modal */}
      <Modal
        isOpen={addModal}
        onClose={() => setAddModal(false)}
        title="Planifier une maintenance"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select
            label="Équipement"
            name="equipmentId"
            value={formData.equipmentId}
            onChange={(e) => setFormData({ ...formData, equipmentId: e.target.value })}
            options={equipment.map((eq) => ({
              value: eq.id,
              label: `${eq.serialNumber} - ${eq.clientName}`,
            }))}
            placeholder="Sélectionner un équipement"
            required
          />

          <Select
            label="Type de maintenance"
            name="type"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            options={[
              { value: 'routine', label: 'Routine' },
              { value: 'inspection', label: 'Inspection' },
              { value: 'repair', label: 'Réparation' },
            ]}
            required
          />

          <Input
            label="Date prévue"
            type="date"
            name="scheduledDate"
            value={formData.scheduledDate}
            onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
            required
          />

          <Textarea
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            placeholder="Observations, instructions pour le technicien..."
          />

          <div className="flex items-center justify-end space-x-3">
            <Button type="button" variant="secondary" onClick={() => setAddModal(false)} disabled={submitting}>
              Annuler
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Planification...' : 'Planifier'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Maintenance Details Modal */}
      <Modal
        isOpen={detailsModal}
        onClose={() => {
          setDetailsModal(false);
          setSelectedMaintenance(null);
        }}
        title="Détails de la maintenance"
      >
        {selectedMaintenance && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
              <div className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                {selectedMaintenance.clientName}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Équipement</label>
              <div className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                {selectedMaintenance.equipmentName || selectedMaintenance.serialNumber}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de maintenance</label>
                <div className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                  {renderCell(selectedMaintenance.type)}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <div className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                  {getStatusLabel(selectedMaintenance.status)}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date prévue</label>
              <div className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                {selectedMaintenance.scheduledDate ? formatDate(selectedMaintenance.scheduledDate) : 'Non définie'}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <div className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 min-h-[80px]">
                {selectedMaintenance.notes || 'Aucune note'}
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4 border-t">
              <Button 
                type="button" 
                variant="secondary" 
                onClick={() => {
                  setDetailsModal(false);
                  setSelectedMaintenance(null);
                }}
              >
                Fermer
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Maintenance Modal */}
      <Modal
        isOpen={editModal}
        onClose={() => {
          setEditModal(false);
          setSelectedMaintenance(null);
          setFormData({ equipmentId: '', type: 'routine', scheduledDate: '', status: 'pending', notes: '' });
        }}
        title="Modifier la maintenance"
      >
        <form onSubmit={handleUpdate} className="space-y-4">
          <Select
            label="Équipement"
            name="equipmentId"
            value={formData.equipmentId}
            onChange={(e) => setFormData({ ...formData, equipmentId: e.target.value })}
            options={equipment.map((eq) => ({
              value: eq.id,
              label: `${eq.serialNumber} - ${eq.clientName}`,
            }))}
            placeholder="Sélectionner un équipement"
            required
          />

          <Select
            label="Type de maintenance"
            name="type"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            options={[
              { value: 'routine', label: 'Routine' },
              { value: 'inspection', label: 'Inspection' },
              { value: 'repair', label: 'Réparation' },
            ]}
            required
          />

          <Input
            label="Date prévue"
            type="date"
            name="scheduledDate"
            value={formData.scheduledDate}
            onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
            required
          />

          <Select
            label="Statut"
            name="status"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            options={[
              { value: 'pending', label: 'En attente' },
              { value: 'scheduled', label: 'Planifiée' },
              { value: 'in_progress', label: 'En cours' },
              { value: 'completed', label: 'Terminée' },
              { value: 'cancelled', label: 'Annulée' },
            ]}
            required
          />

          <Textarea
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            placeholder="Observations, instructions pour le technicien..."
          />

          <div className="flex items-center justify-end space-x-3">
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => {
                setEditModal(false);
                setSelectedMaintenance(null);
                setFormData({ equipmentId: '', type: 'routine', scheduledDate: '', status: 'pending', notes: '' });
              }}
              disabled={submitting}
            >
              Annuler
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, maintenanceId: null, maintenanceName: '' })}
        onConfirm={handleDelete}
        title="Supprimer la maintenance"
        message={`Êtes-vous sûr de vouloir supprimer la maintenance "${deleteConfirm.maintenanceName}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
      />
    </div>
  );
};

export default MaintenanceManagement;

